import type { BookMeta } from '../shared/types';
import { api, AuthError, type BookDetail } from './api';
import { toast } from './toast';

export interface ShelfHooks {
  getBooks(): BookMeta[];
  getBook(): BookDetail | null;
  getChapterId(): string | null;
  openBook(id: string): Promise<void>;
  openChapterById(id: string): void;
  removeBook(id: string): Promise<void>;
  importFile(file: File): Promise<void>;
}

let hooks: ShelfHooks;

function el(id: string): HTMLElement {
  return document.getElementById(id)!;
}

const PALETTES = [
  'linear-gradient(155deg,#B9C0B4 0%,#8E968C 55%,#6E756E 100%)',
  'linear-gradient(155deg,#C4B49A 0%,#A08B6D 55%,#7A6549 100%)',
  'linear-gradient(155deg,#9FA8B8 0%,#6E7A8C 55%,#4E5661 100%)',
  'linear-gradient(155deg,#B89FA8 0%,#96707E 55%,#6E4E5A 100%)',
];

const FORMAT_LABEL: Record<string, string> = { epub: 'EPUB', txt: 'TXT', md: 'MARKDOWN' };

function paletteOf(title: string): string {
  let hash = 0;
  for (const ch of title) hash = (hash * 31 + ch.codePointAt(0)!) >>> 0;
  return PALETTES[hash % PALETTES.length]!;
}

export function renderShelf() {
  const list = el('bookList');
  const activeId = hooks.getBook()?.id ?? null;
  list.innerHTML = '';

  for (const book of hooks.getBooks()) {
    const item = document.createElement('div');
    item.className = `book-item${book.id === activeId ? ' active' : ''}`;
    item.title = book.title;

    const cover = document.createElement('div');
    cover.className = 'cover';
    cover.style.background = paletteOf(book.title);
    const coverText = document.createElement('span');
    coverText.textContent = book.title.slice(0, 4);
    cover.append(coverText);

    const meta = document.createElement('div');
    meta.className = 'book-meta';
    const name = document.createElement('div');
    name.className = 'name';
    name.textContent = book.title;
    const fmt = document.createElement('div');
    fmt.className = 'fmt ui';
    fmt.textContent = `${book.author} · ${FORMAT_LABEL[book.format] ?? book.format}`;
    meta.append(name, fmt);

    const del = document.createElement('button');
    del.className = 'book-del';
    del.textContent = '✕';
    del.title = '移除书籍';
    del.addEventListener('click', (e) => {
      e.stopPropagation();
      if (window.confirm(`把《${book.title}》从书库移除？划线、批注与讨论会一并删除。`)) {
        void hooks.removeBook(book.id);
      }
    });

    const exp = document.createElement('button');
    exp.className = 'book-export';
    exp.textContent = '↓';
    exp.title = '导出 Markdown';
    exp.addEventListener('click', (e) => {
      e.stopPropagation();
      void api.exportBook(book.id, book.title).catch((error) => {
        if (error instanceof AuthError) return; // 遮罩已弹出
        toast(error instanceof Error ? error.message : '导出失败');
      });
    });

    item.append(cover, meta, del, exp);
    item.addEventListener('click', () => void hooks.openBook(book.id));
    list.append(item);
  }

  renderToc();
}

export function renderToc() {
  const wrap = el('tocWrap');
  const toc = el('toc');
  const book = hooks.getBook();
  toc.innerHTML = '';
  if (!book) {
    wrap.hidden = true;
    return;
  }
  wrap.hidden = false;
  const activeChapterId = hooks.getChapterId();
  for (const chapter of book.chapters) {
    const item = document.createElement('span');
    item.className = `toc-item${chapter.id === activeChapterId ? ' active' : ''}`;
    item.textContent = chapter.title;
    item.title = chapter.title;
    item.addEventListener('click', () => hooks.openChapterById(chapter.id));
    toc.append(item);
  }
}

export function initShelf(h: ShelfHooks) {
  hooks = h;
  const importBtn = el('importBtn') as HTMLButtonElement;
  const fileInput = el('fileInput') as HTMLInputElement;

  importBtn.addEventListener('click', () => fileInput.click());
  fileInput.addEventListener('change', () => {
    void (async () => {
      const file = fileInput.files?.[0];
      fileInput.value = '';
      if (!file) return;
      importBtn.disabled = true;
      importBtn.textContent = '导入中…';
      try {
        await hooks.importFile(file);
      } catch (error) {
        toast(error instanceof Error ? error.message : '导入失败');
      } finally {
        importBtn.disabled = false;
        importBtn.textContent = '导入书籍';
      }
    })();
  });

  renderShelf();
}
